import { Injectable } from '@angular/core';
import {
  ApiService,
  DevicesService,
  TestTypeValue,
  CommonRegEx,
  TestLayerValue,
  HelpersService
} from '@nrwl/common-lib';
import { BaseSetupService } from '@nrwl/test-setup';
import { TruespeedConfigFieldMapping } from 'libs/truespeed-test/src/lib/setup/tabs/models/model';
import {
  IPAddressValue,
  Y1564ConfigFieldMapping,
  vlanConfigValue
} from 'libs/y1564-test/src/lib/y1564-test/setup/models/models';
import { ServiceConfigFieldMapping } from '@nrwl/service-test';
import { MultistepConfigMapping } from '@nrwl/multi-step-test';
import { VoipConfigMapping } from '@nrwl/voip-test';
import { RFC2544ConfigFieldMapping } from '@nrwl/rfc2544-test';
import { Y1731ConfigFieldMapping } from '@nrwl/y1731-test';
import { TwampConfigFieldMapping } from '@nrwl/twamp-test';
import { UDPConfigFieldMapping } from '@nrwl/udp-test';
import { TwampReflectorConfigMapping } from '@nrwl/twamp-reflector-test';
import { Observable } from 'rxjs';
import { TwampTestSetupConfigService } from './twamp-test-setup-config.service';
import { HttpConfigFieldMapping } from '@nrwl/http-test';
import { FtpConfigFieldMapping } from '@nrwl/ftp-test';
import { BertConfigFieldMapping } from '@nrwl/bert-test';

@Injectable()
export class TestSetupTemplateService {
  getTemplateConfigURL = '/uicomm/getTestTemplate';
  findTemplateConfigURL = '/uicomm/findTestTemplate';
  templateConfig: any;
  testType: any;
  mode: string;
  templateName: string;

  constructor(
    private readonly apiService: ApiService,
    private readonly deviceService: DevicesService,
    private readonly baseSetupService: BaseSetupService,
    private readonly twampTestSetupConfigService: TwampTestSetupConfigService,
    private readonly helperService: HelpersService
  ) {}

  //eslint-disable-next-line
  getTemplateConfig(templateId: string): Observable<any> {
    const url = `${this.getTemplateConfigURL}/${templateId}`;
    return this.apiService.post$<any>(url);
  }

  //eslint-disable-next-line
  findTemplateConfig(options: any): Observable<any> {
    const url = `${this.findTemplateConfigURL}`;
    return this.apiService.post$<any>(url, options);
  }

  //eslint-disable-next-line
  populateTemplateOutputParams(responseData: any, mode: string): void {
    this.templateConfig = responseData.template;
    this.templateName = responseData.name;
    this.testType = responseData.testType;
    this.mode = mode;
    return this.getOutputParams();
  }

  //eslint-disable-next-line
  getOutputParams(): any {
    let config = {};
    const masterData = this.templateConfig['test-parameters'];
    let commonParams = {};
    if (this.testType === TestTypeValue.SERVICE_CONTINUITY) {
      commonParams = this.getServiceAgentTestParams();
    } else if (this.testType == TestTypeValue.Y1564) {
      commonParams = this.getY1564AgentTestparams();
    } else if (this.testType == TestTypeValue.RFC2544) {
      commonParams = this.getRfc2544TestParams();
      return { ...masterData, ...commonParams };
    } else if (this.testType == TestTypeValue.MULTISTEP) {
      commonParams = this.getMultistepAgentTestparams();
    } else if (this.testType == TestTypeValue.VOIP) {
      commonParams = this.getVoipAgentTestParams();
    } else if (this.testType === TestTypeValue.TRUESPEED) {
      commonParams = this.getTruespeedTestParams();
    } else if (this.testType === 'y1731') {
      commonParams = this.getY1731TestParams();
    } else if (this.testType == TestTypeValue.UDP) {
      commonParams = this.getUdpTestParams();
    } else if (this.testType == TestTypeValue.HTTP) {
      commonParams = this.getHttpTestParams();
    } else if (this.testType == TestTypeValue.FTP) {
      commonParams = this.getFtpTestParams();
    } else if (this.testType == TestTypeValue.BERT) {
      commonParams = this.getBertTestParams();
    } else if (this.testType == TestTypeValue.TWAMP) {
      commonParams = this.getTwampTestParams();
      return { ...masterData, ...commonParams };
    } else if (
      this.testType == TestTypeValue.TWAMP_REFLECTOR ||
      this.testType == TestTypeValue.TWAMP_LIGHT_REFLECTOR
    ) {
      commonParams = this.getTwampReflectorParams();
    } else {
      commonParams = this.getCommonAgentTestparams();
    }
    config = { ...commonParams, ...masterData };
    if (
      config[Y1564ConfigFieldMapping.STEP_PERCENTS] === undefined ||
      config[Y1564ConfigFieldMapping.STEP_PERCENTS] === null
    ) {
      config[Y1564ConfigFieldMapping.STEP_PERCENTS] = [];
    }
    return config;
  }

  // Load common params from the selected device entity.
  getCommonAgentTestparams(): any {
    const config = {};
    config[TruespeedConfigFieldMapping.CUSTOMER] =
      this.templateConfig['test-data']['customer']?.name;
    config[TruespeedConfigFieldMapping.CONTACT] =
      this.templateConfig['test-data']['customer']?.contact;
    config[TruespeedConfigFieldMapping.EMAIL] = this.templateConfig['test-data']['customer']?.email;
    config[TruespeedConfigFieldMapping.PHONE] = this.templateConfig['test-data']['customer']?.phone;
    config[TruespeedConfigFieldMapping.TECHINICIAN] =
      this.templateConfig['test-data']['technician']?.name;
    config[TruespeedConfigFieldMapping.TECHINICIANID] =
      this.templateConfig['test-data']['technician']?.id;
    config[TruespeedConfigFieldMapping.COMMENTS] =
      this.templateConfig['test-data']['testInfo']?.comments;
    config[TruespeedConfigFieldMapping.TEST_MODE] = this.templateConfig['test-data']?.mode;
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      if (Array.isArray(this.templateConfig['agent']['primary'])) {
        const originatorArray = [];
        for (let i = 0; i < this.templateConfig['agent']['primary'].length; i++) {
          const orginatorName = this.templateConfig['agent']?.primary?.[i]?.id;
          const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
          originatorArray.push(orginatorDevice);
          if (originatorArray) {
            config[TruespeedConfigFieldMapping.SELECTED_ORIGINATOR] = originatorArray;
            config[TruespeedConfigFieldMapping.AGENT_TYPE] = originatorArray[i].getModel();
          }
        }
      } else {
        const orginatorName = this.templateConfig['agent']?.primary?.id;
        const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
        if (orginatorDevice) {
          config[TruespeedConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
          config[TruespeedConfigFieldMapping.ENGINE_A_INFO] = this.baseSetupService.getEngineInfo(
            orginatorDevice.getDevice()
          );
          config[TruespeedConfigFieldMapping.AGENT_TYPE] = orginatorDevice.getModel();
        }
        const destinationName = this.templateConfig['agent']?.secondary?.id;
        const destinationDevice = this.deviceService.getDeviceName(destinationName);
        if (destinationDevice) {
          config[TruespeedConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
          config[TruespeedConfigFieldMapping.ENGINE_B_INFO] = this.baseSetupService.getEngineInfo(
            destinationDevice.getDevice()
          );
        }
      }
    }

    return config;
  }

  // eslint-disable-next-line
  getTestParamsById(testId: string, templateData: any): string {
    let optionValue = '';
    const option = templateData.find(opt => opt.id === testId);
    optionValue = option ? option.value : '';
    return optionValue;
  }

  getTwampReflectorParams(): any {
    let config = {};
    config = this.getCommonAgentTestparams();
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      const orginatorName = this.templateConfig['agent']?.primary?.id;
      const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
      const templateData = this.templateConfig['test-parameters'];
      if (orginatorDevice.getDeviceType()?.includes('JMEP')) {
        config[TwampReflectorConfigMapping.UDP_PORT] = templateData['udpPort'];
      } else {
        config[TwampReflectorConfigMapping.REFLECTOR_MODE] =
          this.testType === TestTypeValue.TWAMP_REFLECTOR ? 'full' : 'light';
        config[TwampReflectorConfigMapping.ADDRESS] = this.getTestParamsById(
          'source-ip',
          templateData
        );
        config[TwampReflectorConfigMapping.SUBNET] = this.getTestParamsById('subnet', templateData);
        config[TwampReflectorConfigMapping.GATEWAY] = this.getTestParamsById(
          'gateway',
          templateData
        );
        config[TwampReflectorConfigMapping.REBOOT] =
          this.getTestParamsById('presist', templateData) || false;
        config[TwampReflectorConfigMapping.UDP_PORT] =
          this.getTestParamsById('reflector-udp-port', templateData) || 5000;

        const vlanId = this.getTestParamsById('vlan-id', templateData);
        const cVlanId = this.getTestParamsById('inner-vlan-id', templateData);
        if (vlanId) {
          config[TwampReflectorConfigMapping.SVLAN_ID] = vlanId;
          if (cVlanId) {
            config[TwampReflectorConfigMapping.CVLAN_ID] = cVlanId;
            config[TwampReflectorConfigMapping.ETHER_TYPE] = this.getTestParamsById(
              'tpid',
              templateData
            );
          }
        }
        config[TwampReflectorConfigMapping.VLAN_CONFIG] =
          vlanId && cVlanId
            ? vlanConfigValue.Q_IN_Q
            : vlanId
            ? vlanConfigValue.SINGLE_VLAN
            : vlanConfigValue.UNTAGGED;
        if (this.getTestParamsById('prefix-length', templateData)) {
          //ipv6 load config
          config[TwampReflectorConfigMapping.ADDRESS_IPV6] = this.getTestParamsById(
            'source-ip',
            templateData
          );
          config[TwampReflectorConfigMapping.GATEWAY_V6] = this.getTestParamsById(
            'gateway',
            templateData
          );
          config[TwampReflectorConfigMapping.PREFIX_V6] = this.getTestParamsById(
            'prefix-length',
            templateData
          );
          config[TwampReflectorConfigMapping.IP_VERSION] = 'v6';
        } else {
          config[TwampReflectorConfigMapping.IP_VERSION] = 'v4';
        }
        config[TwampReflectorConfigMapping.SESSIONS] =
          this.getTestParamsById('max-connections', templateData) || 5;
        config[TwampReflectorConfigMapping.FLOWS] =
          this.getTestParamsById('max-flows', templateData) || 50;
        const twampString = this.getTestParamsById('twamp', templateData);
        if (twampString) {
          const twampObj = JSON.parse(twampString);
          config[TwampReflectorConfigMapping.CONTROL_PORT] =
            twampObj['ietf-twamp:twamp']['twamp-server']['server-tcp-port'];
          config[TwampReflectorConfigMapping.SESSIONS] =
            config[TwampReflectorConfigMapping.SESSIONS] ||
            twampObj['ietf-twamp:twamp']['twamp-server']['max-connections'];
          config[TwampReflectorConfigMapping.FLOWS] =
            config[TwampReflectorConfigMapping.FLOWS] ||
            twampObj['ietf-twamp:twamp']['twamp-server']['max-flows'];
        }
      }
    }
    return config;
  }

  getServiceAgentTestParams(): any {
    let config = {};
    config = this.getCommonAgentTestparams();
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      config[ServiceConfigFieldMapping.SELECTED_APP_ID] =
        this.templateConfig['agent']?.secondary?.id;
      const thresholdsObj = this.templateConfig?.['test-parameters']?.['severityThresholds'];
      if (thresholdsObj) {
        config[ServiceConfigFieldMapping.THRESHOLD_CRITICALL] = thresholdsObj[0].threshold;
        config[ServiceConfigFieldMapping.THRESHOLD_WARNING] = thresholdsObj[1].threshold;
      }
    }
    return config;
  }

  getMultistepAgentTestparams(): any {
    let config = {};
    config = this.getCommonAgentTestparams();
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      config[MultistepConfigMapping.SELECTED_APP_ID] = this.templateConfig['agent']?.secondary?.id;
    }
    return config;
  }

  getVoipAgentTestParams(): any {
    let config = {};
    config = this.getCommonAgentTestparams();
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      config[VoipConfigMapping.PROXY_CONFIG] = this.templateConfig['test-parameters'].proxyConfig;
    }
    return config;
  }

  getY1564AgentTestparams(): any {
    let config = {};
    config = this.getCommonAgentTestparams();
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      const orginatorName = this.templateConfig['agent']?.primary?.id;
      const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
      config[Y1564ConfigFieldMapping.ENGINE_A_INFO] =
        this.templateConfig[Y1564ConfigFieldMapping.ENGINE_A_INFO] ??
        config[Y1564ConfigFieldMapping.ENGINE_A_INFO];
      if (orginatorDevice) {
        config[Y1564ConfigFieldMapping.SRC_IPV4_ADDRESS] =
          config[Y1564ConfigFieldMapping.ENGINE_A_INFO]['test-ip'];
        config[Y1564ConfigFieldMapping.SRC_SUBNET] =
          config[Y1564ConfigFieldMapping.ENGINE_A_INFO]['test-subnet-mask'];
        config[Y1564ConfigFieldMapping.SRC_GATEWAY] =
          config[Y1564ConfigFieldMapping.ENGINE_A_INFO]['test-gateway'];
      }
      const destinationName = this.templateConfig['agent']?.secondary?.id;
      const destinationDevice = this.deviceService.getDeviceName(destinationName);
      config[Y1564ConfigFieldMapping.ENGINE_B_INFO] =
        this.templateConfig[Y1564ConfigFieldMapping.ENGINE_B_INFO] ??
        config[Y1564ConfigFieldMapping.ENGINE_B_INFO];
      if (destinationDevice) {
        config[Y1564ConfigFieldMapping.DEST_IPV4_ADDRESS] =
          config[Y1564ConfigFieldMapping.ENGINE_B_INFO]['test-ip'];
        config[Y1564ConfigFieldMapping.DEST_SUBNET] =
          config[Y1564ConfigFieldMapping.ENGINE_B_INFO]['test-subnet-mask'];
        config[Y1564ConfigFieldMapping.DEST_GATEWAY] =
          config[Y1564ConfigFieldMapping.ENGINE_B_INFO]['test-gateway'];
      }
      config[Y1564ConfigFieldMapping.SCHEDULER] = this.templateConfig['test-parameters'].scheduler;
      config[Y1564ConfigFieldMapping.DEVICE_SELECTION_METHOD] =
        this.templateConfig['group'][0][Y1564ConfigFieldMapping.DEVICE_SELECTION_METHOD];
      config[Y1564ConfigFieldMapping.STREAM_NAME] =
        this.templateConfig['group'][0][Y1564ConfigFieldMapping.STREAM_NAME];
      config[Y1564ConfigFieldMapping.STREAMS] = this.getFlatternedStreamData(
        this.templateConfig['group'][0]?.[Y1564ConfigFieldMapping.STREAMS]
      );

      const streamsConfig = {
        ...config[Y1564ConfigFieldMapping.STREAMS][0]['l2-config'],
        ...config[Y1564ConfigFieldMapping.STREAMS][0]['l3-ipv4-config'],
        ...config[Y1564ConfigFieldMapping.STREAMS][0]['l3-ipv6-config']
      };

      config[Y1564ConfigFieldMapping.VLAN_CONFIG] = streamsConfig[Y1564ConfigFieldMapping.CVLAN_ID]
        ? vlanConfigValue.Q_IN_Q
        : streamsConfig[Y1564ConfigFieldMapping.SVLAN_ID]
        ? vlanConfigValue.SINGLE_VLAN
        : vlanConfigValue.UNTAGGED;
      config = {
        ...config,
        ...streamsConfig
      };
    }

    return config;
  }

  getBertTestParams(): any {
    let config = {};
    config = this.getCommonAgentTestparams();
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      const orginatorName = this.templateConfig['agent']?.primary?.id;
      const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
      config[BertConfigFieldMapping.ENGINE_A_INFO] =
        this.templateConfig[BertConfigFieldMapping.ENGINE_A_INFO] ??
        config[BertConfigFieldMapping.ENGINE_A_INFO];
      if (orginatorDevice) {
        config[BertConfigFieldMapping.SRC_IPV4_ADDRESS] =
          config[BertConfigFieldMapping.ENGINE_A_INFO]['test-ip'];
        config[BertConfigFieldMapping.SRC_SUBNET] =
          config[BertConfigFieldMapping.ENGINE_A_INFO]['test-subnet-mask'];
        config[BertConfigFieldMapping.SRC_GATEWAY] =
          config[BertConfigFieldMapping.ENGINE_A_INFO]['test-gateway'];
      }
      const destinationName = this.templateConfig['agent']?.secondary?.id;
      const destinationDevice = this.deviceService.getDeviceName(destinationName);
      config[BertConfigFieldMapping.ENGINE_B_INFO] =
        this.templateConfig[BertConfigFieldMapping.ENGINE_B_INFO] ??
        config[BertConfigFieldMapping.ENGINE_B_INFO];
      if (destinationDevice) {
        config[BertConfigFieldMapping.DEST_IPV4_ADDRESS] =
          config[BertConfigFieldMapping.ENGINE_B_INFO]['test-ip'];
      }
      config[BertConfigFieldMapping.SCHEDULER] = this.templateConfig['test-parameters'].scheduler;
      config[BertConfigFieldMapping.DEVICE_SELECTION_METHOD] =
        this.templateConfig['group'][0][BertConfigFieldMapping.DEVICE_SELECTION_METHOD];
      config[BertConfigFieldMapping.STREAM_NAME] =
        this.templateConfig['group'][0][BertConfigFieldMapping.STREAM_NAME];
      config[BertConfigFieldMapping.STREAMS] = this.getBertFlatternedStreamData(
        this.templateConfig['group'][0]?.[BertConfigFieldMapping.STREAMS]
      );

      const streamsConfig = {
        ...config[BertConfigFieldMapping.STREAMS][0]['l2-config'],
        ...config[BertConfigFieldMapping.STREAMS][0]['l3-ipv4-config']
      };
      config[BertConfigFieldMapping.BERT_PATTERN] =
        config[BertConfigFieldMapping.STREAMS][0][BertConfigFieldMapping.BERT_PATTERN];

      config[BertConfigFieldMapping.VLAN_CONFIG] = streamsConfig[BertConfigFieldMapping.CVLAN_ID]
        ? vlanConfigValue.Q_IN_Q
        : streamsConfig[BertConfigFieldMapping.SVLAN_ID]
        ? vlanConfigValue.SINGLE_VLAN
        : vlanConfigValue.UNTAGGED;
      config = {
        ...config,
        ...streamsConfig
      };
    }

    return config;
  }

  getIpAddressType(ipAddress: string): string {
    if (CommonRegEx.IPV6.test(ipAddress)) {
      return IPAddressValue.IP_V6;
    } else {
      return IPAddressValue.IP_V4;
    }
  }

  getRfc2544TestParams(): any {
    let config = {};
    const templateData = this.templateConfig['test-parameters'];
    config = this.getCommonAgentTestparams();
    // Dont assign agent when loading default template
    if (this.mode === 'template') {
      config[RFC2544ConfigFieldMapping.STREAM] = {};
      config[RFC2544ConfigFieldMapping.TEST_MODE] = 'loopback';
      config[RFC2544ConfigFieldMapping.LAYER_TO_TEST] = this.getLayerType(templateData);
      config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] = this.getIpAddressType(
        templateData['source-ip']
      );
      config[RFC2544ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS] =
        templateData['loopback-device'] === 'other';
      config[RFC2544ConfigFieldMapping.SCHEDULER] = templateData['scheduler'];
      if (config[RFC2544ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
        config[RFC2544ConfigFieldMapping.DESTINATION_MAC] =
          templateData['layer2']['destination-mac'];
        config[RFC2544ConfigFieldMapping.STREAM].dscp = Number(
          templateData['layer2']['tos-or-dscp']
        );
        if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
          config[RFC2544ConfigFieldMapping.SRC_IPV4_ADDRESS] = templateData['source-ip'];
          config[RFC2544ConfigFieldMapping.DEST_IPV4_ADDRESS] =
            templateData['layer2']['destination-ip'];
        } else if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6) {
          config[RFC2544ConfigFieldMapping.SRC_IPV6_ADDRESS] = templateData['source-ip'];
          config[RFC2544ConfigFieldMapping.DEST_IPV6_ADDRESS] =
            templateData['layer2']['destination-ip'];
          config[RFC2544ConfigFieldMapping.STREAM]['flow-label'] =
            this.templateConfig['ipv6StreamConfig']?.['flow-label'];
          config[RFC2544ConfigFieldMapping.STREAM]['traffic-class'] =
            this.templateConfig['ipv6StreamConfig']?.['traffic-class'];
          config[RFC2544ConfigFieldMapping.STREAM]['hop-limit'] =
            this.templateConfig['ipv6StreamConfig']?.['hop-limit'];
        }
      } else if (config[RFC2544ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3) {
        if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
          config[RFC2544ConfigFieldMapping.STREAM].dscp = Number(
            templateData['layer3-ipv4']['tos-or-dscp']
          );
          config[RFC2544ConfigFieldMapping.DEST_IPV4_ADDRESS] =
            templateData['layer3-ipv4']['destination-ip'];
          config[RFC2544ConfigFieldMapping.STREAM]['tos-diff-serv'] =
            templateData['layer3-ipv4']['filter-on-tos'] === 'true' ? true : false;
          config[RFC2544ConfigFieldMapping.SRC_IPV4_ADDRESS] = templateData['source-ip'];
          config[RFC2544ConfigFieldMapping.SRC_SUBNET] = templateData['subnet'];
          config[RFC2544ConfigFieldMapping.SRC_GATEWAY] = templateData['gateway'];
        } else if (config[RFC2544ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6) {
          config[RFC2544ConfigFieldMapping.DEST_IPV4_ADDRESS] =
            templateData['layer3-ipv6']['destination-ip'];
          config[RFC2544ConfigFieldMapping.SRC_IPV6_ADDRESS] = templateData['source-ip'];
          config[RFC2544ConfigFieldMapping.SRC_SUBNET] = templateData['prefix-length'];
          config[RFC2544ConfigFieldMapping.SRC_GATEWAY] = templateData['gateway'];
          config[RFC2544ConfigFieldMapping.STREAM]['flow-label'] =
            templateData['layer3-ipv6']?.['flow-label'];
          config[RFC2544ConfigFieldMapping.STREAM]['traffic-class'] =
            templateData['layer3-ipv6']?.['traffic-class'];
          config[RFC2544ConfigFieldMapping.STREAM]['hop-limit'] =
            templateData['layer3-ipv6']?.['hop-limit'];
        }
      }
      config[RFC2544ConfigFieldMapping.STREAM_RATE_CONVENTION] = templateData['unit-convention'];
      config[RFC2544ConfigFieldMapping.MAX_TEST_DURATION] = templateData['test-timeout'];
      config[RFC2544ConfigFieldMapping.STREAM]['frame-size-list'] =
        this.getRFCFrameSize(templateData);
      config[RFC2544ConfigFieldMapping.STREAM]['cir-from-engine-a'] = templateData['cir'] / 1000;
      config[RFC2544ConfigFieldMapping.STREAM]['tos-diff-serv'] = templateData['tos-diff-serv'];
      config[RFC2544ConfigFieldMapping.NETWORK_OVERHEAD] = Number(templateData['network-overhead']);
      if (templateData['throughput']) {
        const throughputData = templateData['throughput'];
        config[RFC2544ConfigFieldMapping.THROUGHPUT] = true;
        config[RFC2544ConfigFieldMapping.THROUGHTPUT_STOP_OVERALL_TEST_ON_FAILURE] =
          throughputData['stop-on-throughput-fail'] === 'true' ? true : false;
        config[RFC2544ConfigFieldMapping.THROUGHPUT_DURATION] = Number(throughputData['duration']);
        config[RFC2544ConfigFieldMapping.THROUGHPUT_BANDWIDTH] = Number(
          throughputData['bw-accuracy']
        );
        config[RFC2544ConfigFieldMapping.THROUGHPUT_FRAME_LOSS_TOLERANCE] = Number(
          throughputData['max-frame-loss']
        );
        config[RFC2544ConfigFieldMapping.THROUGHPUT_STARTING_UTILIZATION] = Number(
          throughputData['start-utilization']
        );
        config[RFC2544ConfigFieldMapping.THROUGHPUT_MAX_UTILIZATION] = Number(
          throughputData['success-utilization-high']
        );
        config[RFC2544ConfigFieldMapping.THROUGHPUT_MIN_UTILIZATION] = Number(
          throughputData['success-utilization-low']
        );
      } else {
        config[RFC2544ConfigFieldMapping.THROUGHPUT] = false;
      }
      if (templateData['latency-fdv']) {
        const latencyData = templateData['latency-fdv'];
        config[RFC2544ConfigFieldMapping.LATENCY] = true;
        config[RFC2544ConfigFieldMapping.LATENCY_NUM_OF_TRIALS] = Number(latencyData['trials']);
        config[RFC2544ConfigFieldMapping.LATENCY_DURATION] = Number(latencyData['duration']);
        config[RFC2544ConfigFieldMapping.TARGET_LATENCY] = Number(latencyData['latency-max']);
        config[RFC2544ConfigFieldMapping.TARGET_FDV] = Number(latencyData['fdv-max']);
        config[RFC2544ConfigFieldMapping.LATENCY_UTILIZATION] = Number(
          latencyData['latency-utilization-percent']
        );
      } else {
        config[RFC2544ConfigFieldMapping.LATENCY] = false;
      }
      if (templateData['frameloss']) {
        const framelossData = templateData['frameloss'];
        config[RFC2544ConfigFieldMapping.FRAME_LOSS] = true;
        config[RFC2544ConfigFieldMapping.FRAME_LOSS_RUN_THORUGHPUT_FAILS] =
          framelossData['skip-if-throughput-pass'] === 'true' ? true : false;
        config[RFC2544ConfigFieldMapping.FRAME_LOSS_DURATION] = Number(framelossData['duration']);
        config[RFC2544ConfigFieldMapping.FRAME_LOSS_MAX_LOSS_ALLOWED] = Number(
          framelossData['max-percent']
        );
        config[RFC2544ConfigFieldMapping.FRAME_LOSS_REDUCE_UTILIZATION] = Number(
          framelossData['utilization-granularity']
        );
      } else {
        config[RFC2544ConfigFieldMapping.FRAME_LOSS] = false;
      }

      if (templateData['bb-cbs']) {
        const b2bCbsData = templateData['bb-cbs'];
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK] = true;
        if (b2bCbsData['bb']) {
          config[RFC2544ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE] = 'b2b';
          config[RFC2544ConfigFieldMapping.BACK_TO_BACK_REPETITIONS] = Number(
            b2bCbsData['bb']['trials']
          );
          config[RFC2544ConfigFieldMapping.BACK_TO_BACK_TARGET_BURST_DURATION] = Number(
            b2bCbsData['bb']['burst-duration']
          );
          config[RFC2544ConfigFieldMapping.BACK_TO_BACK_MAX_TIME_RECEIVE_BURST] = Number(
            b2bCbsData['bb']['receive-time']
          );
          config[RFC2544ConfigFieldMapping.BACK_TO_BACK_BURST_RESOLUTION] = Number(
            b2bCbsData['bb']['granularity']
          );
        } else if (b2bCbsData['cbs']) {
          config[RFC2544ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE] = 'cbs';
          config[RFC2544ConfigFieldMapping.BACK_TO_BACK_REPETITIONS] = Number(
            b2bCbsData['cbs']['trials']
          );
          config[RFC2544ConfigFieldMapping.CBS_COMMITTED_BURST_SIZE] = Number(
            b2bCbsData['cbs']['burst-size']
          );
          config[RFC2544ConfigFieldMapping.CBS_KILO_BYTE_SIZE] = Number(
            b2bCbsData['cbs']['burst-size-multiplier']
          );
        }
      } else {
        config[RFC2544ConfigFieldMapping.BACK_TO_BACK] = false;
      }

      //Vlans
      const vlanId = templateData['vlan-id'] || null;
      const cVlanId = templateData['inner-vlan-id'] || null;
      if (vlanId) {
        config[RFC2544ConfigFieldMapping.SVLAN_ID] = vlanId;
        config[RFC2544ConfigFieldMapping.SVLAN_PRIORITY] = templateData['vlan-priority'] || '0';
        if (cVlanId) {
          config[RFC2544ConfigFieldMapping.CVLAN_ID] = cVlanId;
          config[RFC2544ConfigFieldMapping.CVLAN_PRIORITY] =
            templateData['inner-vlan-priority'] || '0';
        }
      }
      config[RFC2544ConfigFieldMapping.VLAN_CONFIG] =
        vlanId && cVlanId
          ? vlanConfigValue.Q_IN_Q
          : vlanId
          ? vlanConfigValue.SINGLE_VLAN
          : vlanConfigValue.UNTAGGED;
    }
    return config;
  }

  //eslint-disable-next-line
  getRFCFrameSize(testConfig: any): string {
    let selectedFrames = '';
    const frameList = testConfig['frame-size-info'];
    Object.keys(frameList).forEach(key => {
      if (frameList[key] === 'true') {
        const size = key.split('use-frame-size-');
        selectedFrames = selectedFrames + size[1] + ',';
      } else if (key === 'custom-frame-size') {
        selectedFrames = selectedFrames + 'custom:' + frameList[key] + ',';
      }
    });
    return selectedFrames;
  }

  //eslint-disable-next-line
  getLayerType(templateData: any): string {
    if (templateData['layer2'] && Object.keys(templateData['layer2']).length) {
      return TestLayerValue.L2;
    } else {
      return TestLayerValue.L3;
    }
  }

  //eslint-disable-next-line
  getTruespeedTestParams(): any {
    let config = {};
    const TruespeedConfigMapping = TruespeedConfigFieldMapping;
    config = this.getCommonAgentTestparams();
    if (this.mode === 'template') {
      const orginatorName = this.templateConfig['agent']?.primary?.id;
      const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
      if (orginatorDevice) {
        config[TruespeedConfigMapping.SRC_TEST_IP_ADDRESS] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_A_INFO]['test-ip'];
        config[TruespeedConfigMapping.SRC_SUBNET] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_A_INFO]['test-subnet-mask'];
        config[TruespeedConfigMapping.SRC_GATEWAY] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_A_INFO]['test-gateway'];
        config[TruespeedConfigMapping.SRC_TRANSMIT_INTERFACE] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_A_INFO]['tx-interface'];
      }
      const destinationName = this.templateConfig['agent']?.secondary?.id;
      const destinationDevice = this.deviceService.getDeviceName(destinationName);
      if (destinationDevice) {
        config[TruespeedConfigMapping.DEST_TEST_IP_ADDRESS] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_B_INFO]['test-ip'];
        config[TruespeedConfigMapping.DEST_SUBNET] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_B_INFO]['test-subnet-mask'];
        config[TruespeedConfigMapping.DEST_GATEWAY] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_B_INFO]['test-gateway'];
        config[TruespeedConfigMapping.DEST_TRANSMIT_INTERFACE] =
          this.templateConfig[TruespeedConfigFieldMapping.ENGINE_B_INFO]['tx-interface'];
      }
      config[TruespeedConfigMapping.SCHEDULER] = this.templateConfig['test-parameters'].scheduler;
    }
    return config;
  }

  getFlatternedStreamData(streamData: Array<any>): Array<any> {
    const flatternedStreamData = [];
    const ipaddressType = this.templateConfig['test-parameters']?.['ip-address-type'];
    streamData.forEach(stream => {
      if (!stream[Y1564ConfigFieldMapping.CBS] && ipaddressType === IPAddressValue.IP_V6) {
        stream[Y1564ConfigFieldMapping.CBS] = 0;
        stream[Y1564ConfigFieldMapping.KB_SIZE] = 'cbs1000';
        stream[Y1564ConfigFieldMapping.CBS_REPETITIONS] = 1;
        stream[Y1564ConfigFieldMapping.CBS_LINE_RATE] = 'Mbps1000';
      }
      const data = {
        ...stream,
        ...stream['l2-config'],
        ...stream['l3-ipv4-config'],
        ...stream['l3-ipv6-config']
      };
      flatternedStreamData.push(data);
    });
    return flatternedStreamData;
  }

  getBertFlatternedStreamData(streamData: Array<any>): Array<any> {
    const flatternedStreamData = [];
    streamData.forEach(stream => {
      const data = {
        ...stream,
        ...stream['l2-config'],
        ...stream['l3-ipv4-config']
      };
      flatternedStreamData.push(data);
    });
    return flatternedStreamData;
  }

  getY1731TestParams(): any {
    let config = {};
    const templateData = this.templateConfig['test-parameters'];
    config = this.getCommonAgentTestparams();
    if (this.mode === 'template') {
      config[Y1731ConfigFieldMapping.SRC_MAC_ADDRESS] = templateData['mac-address'];
      config[Y1731ConfigFieldMapping.PM_REPORT_INTERVAL] = templateData['interval'];
      config[Y1731ConfigFieldMapping.MEP_ID] = templateData['mep-id'];
      config[Y1731ConfigFieldMapping.FLOWS] = templateData['flow'];
      config[Y1731ConfigFieldMapping.DESTINATION_MAC] = templateData['flow'][0]['remote-mac-addr'];
      config[Y1731ConfigFieldMapping.FLOW_ID] = templateData['flow'][0]['flow-id'];
      config[Y1731ConfigFieldMapping.PERSIST_AFTER_REBOOT] =
        templateData['persist']?.toString() === 'true' ? true : false;
      const vlanId = templateData['vlan-id'] || null;
      const cVlanId = templateData['inner-vlan-id'] || null;
      if (vlanId) {
        config[RFC2544ConfigFieldMapping.SVLAN_ID] = vlanId;
        config[RFC2544ConfigFieldMapping.SVLAN_PRIORITY] = templateData['vlan-priority'] || '0';
        if (cVlanId) {
          config[RFC2544ConfigFieldMapping.CVLAN_ID] = cVlanId;
          config[RFC2544ConfigFieldMapping.CVLAN_PRIORITY] =
            templateData['inner-vlan-priority'] || '0';
          config[RFC2544ConfigFieldMapping.CVLAN_ETHER_TYPE] = templateData['tpid'];
        }
      }
      config[RFC2544ConfigFieldMapping.VLAN_CONFIG] =
        vlanId && cVlanId
          ? vlanConfigValue.Q_IN_Q
          : vlanId
          ? vlanConfigValue.SINGLE_VLAN
          : vlanConfigValue.UNTAGGED;
    }
    return config;
  }

  getHttpTestParams(): any {
    let config = {};
    const templateData = this.templateConfig['test-parameters'];
    config = this.getCommonAgentTestparams();
    if (this.mode === 'template') {
      const httpTestParams = templateData['http-parameters'];
      const orginatorName = this.templateConfig['agent']?.primary?.id;
      if (orginatorName) {
        const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
        if (orginatorDevice) {
          config[HttpConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
        }
      }
      const destinationName = this.templateConfig['agent']?.secondary?.id;
      if (destinationName) {
        const destinationDevice = this.deviceService.getDeviceName(destinationName);
        if (destinationDevice) {
          config[HttpConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
        }
      }
      config[HttpConfigFieldMapping.TEST_MODE] = httpTestParams[HttpConfigFieldMapping.TEST_MODE];
      config[HttpConfigFieldMapping.DIRECTION] = httpTestParams[HttpConfigFieldMapping.DIRECTION];
      config[HttpConfigFieldMapping.THRESHOLD_ENABLED] =
        httpTestParams[HttpConfigFieldMapping.THRESHOLD_ENABLED];
      config[HttpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD] =
        httpTestParams[HttpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD];
      config[HttpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD] =
        httpTestParams[HttpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD];
      config[HttpConfigFieldMapping.TARGET_ENABLED] =
        httpTestParams[HttpConfigFieldMapping.TARGET_ENABLED];
      config[HttpConfigFieldMapping.ENGINE_A_TO_B_TARGET] =
        httpTestParams[HttpConfigFieldMapping.ENGINE_A_TO_B_TARGET];
      config[HttpConfigFieldMapping.ENGINE_B_TO_A_TARGET] =
        httpTestParams[HttpConfigFieldMapping.ENGINE_B_TO_A_TARGET];
      config[HttpConfigFieldMapping.ENGINE_A_TO_B_URL] =
        httpTestParams[HttpConfigFieldMapping.ENGINE_A_TO_B_URL];
      config[HttpConfigFieldMapping.ENGINE_B_TO_A_URL] =
        httpTestParams[HttpConfigFieldMapping.ENGINE_B_TO_A_URL];
      config[HttpConfigFieldMapping.TEST_TYPE] = httpTestParams[HttpConfigFieldMapping.TEST_TYPE];
      config[HttpConfigFieldMapping.TCP_PORT] = httpTestParams[HttpConfigFieldMapping.TCP_PORT];
      config[HttpConfigFieldMapping.RUN_TYPE] = httpTestParams[HttpConfigFieldMapping.RUN_TYPE];
      config[HttpConfigFieldMapping.SCHEDULER] = templateData['scheduler'];
    }
    return config;
  }

  getFtpTestParams(): any {
    let config = {};
    const templateData = this.templateConfig['test-parameters'];
    config = this.getCommonAgentTestparams();
    if (this.mode === 'template') {
      const ftpTestParams = templateData['ftp-parameters'];
      const orginatorName = this.templateConfig['agent']?.primary?.id;
      if (orginatorName) {
        const orginatorDevice = this.deviceService.getDeviceName(orginatorName);
        if (orginatorDevice) {
          config[FtpConfigFieldMapping.SELECTED_ORIGINATOR] = orginatorDevice;
        }
      }
      const destinationName = this.templateConfig['agent']?.secondary?.id;
      if (destinationName) {
        const destinationDevice = this.deviceService.getDeviceName(destinationName);
        if (destinationDevice) {
          config[FtpConfigFieldMapping.SELECTED_DESTINATION] = destinationDevice;
        }
      }
      config[FtpConfigFieldMapping.TEST_MODE] = ftpTestParams[FtpConfigFieldMapping.TEST_MODE];
      config[FtpConfigFieldMapping.DIRECTION] = ftpTestParams[FtpConfigFieldMapping.DIRECTION];
      config[FtpConfigFieldMapping.THRESHOLD_ENABLED] =
        ftpTestParams[FtpConfigFieldMapping.THRESHOLD_ENABLED];
      config[FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD] =
        ftpTestParams[FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD];
      config[FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD] =
        ftpTestParams[FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD];
      config[FtpConfigFieldMapping.TARGET_ENABLED] =
        ftpTestParams[FtpConfigFieldMapping.TARGET_ENABLED];
      config[FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET] =
        ftpTestParams[FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET];
      config[FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET] =
        ftpTestParams[FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET];
      config[FtpConfigFieldMapping.ENGINE_A_TO_B_URL] =
        ftpTestParams[FtpConfigFieldMapping.ENGINE_A_TO_B_URL];
      config[FtpConfigFieldMapping.ENGINE_B_TO_A_URL] =
        ftpTestParams[FtpConfigFieldMapping.ENGINE_B_TO_A_URL];
      config[FtpConfigFieldMapping.TEST_TYPE] = ftpTestParams[FtpConfigFieldMapping.TEST_TYPE];
      config[FtpConfigFieldMapping.TCP_PORT] = ftpTestParams[FtpConfigFieldMapping.TCP_PORT];
      config[FtpConfigFieldMapping.RUN_TYPE] = ftpTestParams[FtpConfigFieldMapping.RUN_TYPE];
      config[FtpConfigFieldMapping.SCHEDULER] = templateData['scheduler'];
    }
    return config;
  }

  getUdpTestParams(): any {
    let config = {};
    config = this.getCommonAgentTestparams();
    if (this.mode === 'template') {
      config[UDPConfigFieldMapping.TEST_MODE] = this.templateConfig?.['test-data']?.mode;
      const streams =
        this.templateConfig?.['extended-config']?.connections?.connections[0]?.streams;
      if (streams?.length > 0) {
        const direction = streams[0]?.strmUdpDirection;
        if (direction === 'engine-a-to-engine-b' || direction === 'unidirectional-a2b') {
          config[UDPConfigFieldMapping.DIRECTION] = 'unidirectional-a2b';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getStreamsData(
            this.templateConfig?.['extended-config'],
            'A2B'
          );
        } else if (direction === 'engine-b-to-engine-a' || direction === 'unidirectional-b2a') {
          config[UDPConfigFieldMapping.DIRECTION] = 'unidirectional-b2a';
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getStreamsData(
            this.templateConfig?.['extended-config'],
            'B2A'
          );
        } else if (direction === 'bidirectional' || direction === 'concurrent-bidirectional') {
          config[UDPConfigFieldMapping.DIRECTION] = 'concurrent-bidirectional';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getStreamsData(
            this.templateConfig?.['extended-config'],
            'A2B'
          );
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getStreamsData(
            this.templateConfig?.['extended-config'],
            'B2A'
          );
        } else {
          config[UDPConfigFieldMapping.DIRECTION] = 'sequential-bidirectional';
          config[UDPConfigFieldMapping.STREAMS_A2B] = this.getStreamsData(
            this.templateConfig?.['extended-config'],
            'A2B'
          );
          config[UDPConfigFieldMapping.STREAMS_B2A] = this.getStreamsData(
            this.templateConfig?.['extended-config'],
            'B2A'
          );
        }
      }
      config[UDPConfigFieldMapping.EXPIRATION_DATE] =
        this.templateConfig?.controller?.['expiration-date'] || null;
      config[UDPConfigFieldMapping.MAX_EXECUTION_COUNTS] =
        this.templateConfig?.controller?.['max-exec-count'] || null;
      const extendedSetup = this.templateConfig?.['extended-config']?.setup;
      if (this.templateConfig?.['extended-config']?.TestDuration === 'custom') {
        config[UDPConfigFieldMapping.TEST_DURATION] = 'custom';
        config[UDPConfigFieldMapping.TEST_CUSTOM_DURATION] = this.helperService.convertSecondsToHMS(
          this.templateConfig?.['test-parameters']?.['udp-parameters']?.duration
        );
      } else {
        config[UDPConfigFieldMapping.TEST_DURATION] =
          this.templateConfig?.['test-parameters']?.['udp-parameters']?.duration;
      }
      if (extendedSetup) {
        if (extendedSetup?.bandwidthConvention === 'ir') {
          config[UDPConfigFieldMapping.STREAM_RATE_CONVENTION] = 'l2-mbps';
        } else {
          config[UDPConfigFieldMapping.STREAM_RATE_CONVENTION] = 'l1-mbps';
        }
        if (extendedSetup?.vlanPresent === 'No' || extendedSetup?.vlanPresent === 46) {
          config[UDPConfigFieldMapping.VLAN] = 46;
        } else if (extendedSetup?.vlanPresent === 'Yes' || extendedSetup?.vlanPresent === 50) {
          config[UDPConfigFieldMapping.VLAN] = 50;
        } else {
          config[UDPConfigFieldMapping.VLAN] = 54;
        }
      }
      config[UDPConfigFieldMapping.SCHEDULER] = this.templateConfig['test-parameters'].scheduler;
      return config;
    }
  }
  // eslint-disable-next-line
  getStreamsData(testConfig, tableChange: string): any {
    const allStreams = testConfig?.connections?.connections[0]?.streams;
    const a2bStreamList = [];
    const b2aStreamList = [];
    let filteredStreams = [];
    const a2bStreamNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    allStreams.forEach(item => {
      if (a2bStreamNumbers.includes(item.strmNumber)) {
        a2bStreamList.push(item);
      } else {
        b2aStreamList.push(item);
      }
    });
    if (tableChange === 'A2B') {
      filteredStreams = a2bStreamList;
    } else {
      filteredStreams = b2aStreamList;
    }
    let newStreams = [];
    const streamObj = {};
    if (filteredStreams?.length > 0) {
      filteredStreams.forEach(stream => {
        streamObj[UDPConfigFieldMapping.STREAM_NUMBER] = stream.strmNumber;
        streamObj[UDPConfigFieldMapping.STREAM_NAME] = stream.strmName;
        streamObj[UDPConfigFieldMapping.STREAM_TYPE] = stream.strmUdpType;
        streamObj[UDPConfigFieldMapping.VIDEO_TYPE] = stream.strmVideoType;
        streamObj[UDPConfigFieldMapping.COMPRESSION] = stream.strmVideoCompression;
        streamObj[UDPConfigFieldMapping.CHANNELS] = stream.strmNumberChannels;
        streamObj[UDPConfigFieldMapping.CODEC] = stream.strmVoiceCodec;
        streamObj[UDPConfigFieldMapping.SAMPLING_RATE] = stream.strmVoiceSampleRate;
        streamObj[UDPConfigFieldMapping.CALLS] = stream.strmNumberCalls;
        streamObj[UDPConfigFieldMapping.STREAM_L2_IR_A2B] = stream.strmSlaULROrIR;
        streamObj[UDPConfigFieldMapping.UDP_BW_A2B] = stream.strmSlaCir;
        streamObj[UDPConfigFieldMapping.STREAM_L2_IR_B2A] = stream.strmSlaULROrIR2;
        streamObj[UDPConfigFieldMapping.UDP_BW_B2A] = stream.strmSlaCir2;
        streamObj[UDPConfigFieldMapping.JITTER_THRESHOLD] = stream.strmFrameJitter;
        streamObj[UDPConfigFieldMapping.FRAME_SIZE] = stream.strmFrameSize;
        streamObj[UDPConfigFieldMapping.POCKET_LOSS_RATIO] = stream.strmPacketLossRatio;
        streamObj[UDPConfigFieldMapping.UDP_PORT] = stream.strmUdpPort;
        streamObj[UDPConfigFieldMapping.STREAM_IN_USE] = stream.strmInUse;
        newStreams = [...newStreams, { ...streamObj }];
      });
    }
    return newStreams;
  }

  getTwampTestParams(): any {
    const config = {};
    const commonParams = this.getCommonAgentTestparams();
    const templateData = this.templateConfig['test-parameters'];
    const vlanId = templateData['vlan-id'] || null;
    const cVlanId = templateData['inner-vlan-id'] || null;
    if (vlanId) {
      config[TwampConfigFieldMapping.SVLAN_ID] = vlanId;
      config[TwampConfigFieldMapping.SVLAN_PRIORITY] = templateData['vlan-priority'] || '0';
      if (cVlanId) {
        config[TwampConfigFieldMapping.CVLAN_ID] = cVlanId;
        config[TwampConfigFieldMapping.CVLAN_PRIORITY] = templateData['inner-vlan-priority'] || '0';
        config[TwampConfigFieldMapping.CVLAN_ETHER_TYPE] = templateData['tpid'];
      }
    }
    config[TwampConfigFieldMapping.VLAN_CONFIG] =
      vlanId && cVlanId
        ? vlanConfigValue.Q_IN_Q
        : vlanId
        ? vlanConfigValue.SINGLE_VLAN
        : vlanConfigValue.UNTAGGED;
    let testOptions = templateData['twamp'];
    let fullConnection = [];
    if (testOptions) {
      const map = templateData['test-session-map'];
      fullConnection = this.twampTestSetupConfigService.processControllerFullConfig(
        testOptions,
        map
      );
    }
    let lightConnection = [];
    testOptions = templateData['twamp-light'];
    if (testOptions) {
      const map = templateData['session-map'];
      const groupmap = templateData['session-group-map'];
      lightConnection = this.twampTestSetupConfigService.processControllerLightConfig(
        testOptions,
        map,
        groupmap
      );
    }
    const combinedConnection = [...fullConnection, ...lightConnection];
    config[TwampConfigFieldMapping.SELECTED_TAB] = templateData['selectedTab'];
    let selectedTab = templateData['selectedTab'];
    selectedTab = selectedTab >= 0 ? selectedTab : 1;
    config[TwampConfigFieldMapping.PM_INTERVAL] = Number(templateData['interval']);

    config[TwampConfigFieldMapping.SUBNET] =
      templateData['subnet'] ?? templateData['prefix-length'];
    config[TwampConfigFieldMapping.GATEWAY] = templateData['gateway'];
    config[TwampConfigFieldMapping.KEEP_ALIVE] =
      templateData['twamp-keep-alive-interval'] && templateData['twamp-keep-alive-interval'] !== '0'
        ? true
        : false;
    const restartOnBootOption = templateData['twamp-restart-on-boot'] || templateData['persist'];
    config[TwampConfigFieldMapping.REBOOT] = restartOnBootOption ? true : false;
    config[TwampConfigFieldMapping.ENABLE_PERCENTILES] =
      templateData['enable-percentiles'] && templateData['enable-percentiles'] === 'true'
        ? true
        : false;
    if (templateData['codec']) {
      config[TwampConfigFieldMapping.CODEC] = templateData['codec'];
    }
    const specificFlows = this.twampTestSetupConfigService.getSpecificIpflows(combinedConnection);
    if (selectedTab === 1) {
      config[TwampConfigFieldMapping.SPECIFIC_FLOWS] = specificFlows;
    } else {
      config[TwampConfigFieldMapping.COMMON_FLOWS] = combinedConnection?.[0].flows;
    }
    config[TwampConfigFieldMapping.SELECTED_REFLECTOR_IDS] = templateData['selectedReflectedIds'];
    config[TwampConfigFieldMapping.REFLECTOR_UDP] = templateData['reflector-udp'];
    const destinationDetails =
      this.twampTestSetupConfigService.getDestinationReflectorDetails(combinedConnection);
    config[TwampConfigFieldMapping.ADDRESS] = combinedConnection?.[0]?.intiatorIp;
    config[TwampConfigFieldMapping.IP_ADDRESS_TYPE] = this.getIpAddressType(
      combinedConnection?.[0]?.intiatorIp
    );
    config[TwampConfigFieldMapping.BASE_UDP] = combinedConnection?.[0]?.baseUdp;
    config[TwampConfigFieldMapping.SELECTED_DESTINATION] =
      this.twampTestSetupConfigService.frameReflectorObject(destinationDetails);
    const allParams = { ...commonParams, ...config };
    return allParams;
  }
}
